import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Upload, CheckCircle, FileSpreadsheet, Download, ChevronRight, ArrowRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';

const ACCENT = '#6366f1';
const MONTHS = [
  { value: '01', label: 'Januari', short: 'jan' },
  { value: '02', label: 'Februari', short: 'feb' },
  { value: '03', label: 'Maart', short: 'mrt' },
  { value: '04', label: 'April', short: 'apr' },
  { value: '05', label: 'Mei', short: 'mei' },
  { value: '06', label: 'Juni', short: 'jun' },
  { value: '07', label: 'Juli', short: 'jul' },
  { value: '08', label: 'Augustus', short: 'aug' },
  { value: '09', label: 'September', short: 'sep' },
  { value: '10', label: 'Oktober', short: 'okt' },
  { value: '11', label: 'November', short: 'nov' },
  { value: '12', label: 'December', short: 'dec' },
];

const BALANS_OPTIES = [
  { code: '1700', naam: 'Vooruitbetaalde kosten' },
  { code: '1710', naam: 'Overlopende activa' },
  { code: '1720', naam: 'Nog te ontvangen bedragen' },
];

function fmt(n) {
  if (n === 0 || n === null || n === undefined) return '€ 0,00';
  return n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
}

function fmtNum(n) {
  if (n === 0 || n === null || n === undefined) return '0,00';
  return n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function extractGBCode(gb) {
  if (!gb) return '';
  const idx = gb.indexOf(' - ');
  return idx > 0 ? gb.substring(0, idx).trim() : gb.trim();
}

function extractGBNaam(gb) {
  if (!gb) return '';
  const idx = gb.indexOf(' - ');
  return idx > 0 ? gb.substring(idx + 3).trim() : '';
}

function isKostenrekening(gb) {
  const code = extractGBCode(gb);
  if (!code) return false;
  const firstChar = code.replace(/\D/g, '')[0];
  if (!firstChar) return false;
  const n = parseInt(firstChar);
  if (n >= 4 && n <= 9) {
    const numCode = code.replace(/\D/g, '');
    if (['1770', '1771', '1780'].includes(numCode)) return false;
    if (numCode.startsWith('113') || numCode.startsWith('130')) return false;
    return true;
  }
  return false;
}

const STEPS = [
  { num: 1, label: 'Upload Excel' },
  { num: 2, label: 'Selecteer kostenregel' },
  { num: 3, label: 'Instellingen' },
  { num: 4, label: 'Memoriaalschema' },
];

export default function SpreidingstoolTab() {
  const [step, setStep] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const fileRef = useRef(null);

  // Step 3 settings
  const now = new Date();
  const [aantalMaanden, setAantalMaanden] = useState(12);
  const [startMaand, setStartMaand] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [startJaar, setStartJaar] = useState(now.getFullYear());
  const [kostenRekening, setKostenRekening] = useState('');
  const [kostenNaam, setKostenNaam] = useState('');
  const [balansCode, setBalansCode] = useState('1700');
  const [boekingen, setBoekingen] = useState([]);
  const [exporting, setExporting] = useState(false);

  const selectedRow = selectedIdx >= 0 ? rows[selectedIdx] : null;
  const bedrag = selectedRow ? (selectedRow.debet || selectedRow.credit || 0) : 0;
  const omschrijving = selectedRow?.omschrijving || '';

  const balansNaam = BALANS_OPTIES.find(b => b.code === balansCode)?.naam || '';

  // Upload handler
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setLoading(true);
    setFileName(file.name);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/v2/parse/spreiding', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      setMetadata(d.metadata);
      setRows(d.rows || []);
      // Auto-select first kostenrekening
      const firstKosten = (d.rows || []).findIndex(r => isKostenrekening(r.grootboek));
      if (firstKosten >= 0) {
        setSelectedIdx(firstKosten);
        setKostenRekening(extractGBCode(d.rows[firstKosten].grootboek));
        setKostenNaam(extractGBNaam(d.rows[firstKosten].grootboek));
      }
      setStep(2);
      toast.success(`${d.total_rows} regels geparsed uit ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload mislukt');
    } finally {
      setLoading(false);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  // Select row handler
  const handleSelectRow = (idx) => {
    setSelectedIdx(idx);
    const row = rows[idx];
    setKostenRekening(extractGBCode(row.grootboek));
    setKostenNaam(extractGBNaam(row.grootboek));
  };

  // Generate boekingen
  const generateBoekingen = useCallback(() => {
    if (!bedrag || bedrag === 0) return;
    const n = aantalMaanden;
    const maandBedrag = Math.floor((bedrag / n) * 100) / 100;
    const rest = Math.round((bedrag - maandBedrag * n) * 100) / 100;

    const result = [];
    const sm = parseInt(startMaand);
    const sj = parseInt(startJaar);

    // Boeking 0 — Overboeking
    const overboekDatum = `01-${String(sm).padStart(2, '0')}-${sj}`;
    result.push({
      datum: overboekDatum,
      type: 'Overboeking',
      omschrijving: `Spreiding ${omschrijving} (${n} mnd)`,
      rekening_code: balansCode,
      rekening_naam: balansNaam,
      debet: bedrag,
      credit: 0,
    });
    result.push({
      datum: overboekDatum,
      type: 'Overboeking',
      omschrijving: `Spreiding ${omschrijving} (${n} mnd)`,
      rekening_code: kostenRekening,
      rekening_naam: kostenNaam,
      debet: 0,
      credit: bedrag,
    });

    // Boekingen 1-N — Maandelijkse vrijval
    for (let i = 0; i < n; i++) {
      let m = sm + i;
      let y = sj;
      while (m > 12) { m -= 12; y += 1; }
      const maandLabel = MONTHS.find(mo => mo.value === String(m).padStart(2, '0'))?.short || '';
      const datum = `01-${String(m).padStart(2, '0')}-${y}`;
      const isLast = i === n - 1;
      const bedragDeze = isLast ? Math.round((maandBedrag + rest) * 100) / 100 : maandBedrag;

      result.push({
        datum,
        type: 'Vrijval',
        omschrijving: `Vrijval ${maandLabel}-${y} ${omschrijving}`,
        rekening_code: kostenRekening,
        rekening_naam: kostenNaam,
        debet: bedragDeze,
        credit: 0,
      });
      result.push({
        datum,
        type: 'Vrijval',
        omschrijving: `Vrijval ${maandLabel}-${y} ${omschrijving}`,
        rekening_code: balansCode,
        rekening_naam: balansNaam,
        debet: 0,
        credit: bedragDeze,
      });
    }

    setBoekingen(result);
    setStep(4);
  }, [bedrag, aantalMaanden, startMaand, startJaar, kostenRekening, kostenNaam, balansCode, balansNaam, omschrijving]);

  // Totals
  const totals = useMemo(() => {
    const d = boekingen.reduce((s, b) => s + (b.debet || 0), 0);
    const c = boekingen.reduce((s, b) => s + (b.credit || 0), 0);
    return { debet: d, credit: c, balanced: Math.abs(d - c) < 0.01 };
  }, [boekingen]);

  // Export Excel — Format 1: Datum|Dagboek|Grootboekrekening|Bedrag|Omschrijving
  const handleExport = async () => {
    setExporting(true);
    try {
      // Convert boekingen to Format 1 rows
      const exportRows = boekingen.map(b => ({
        datum: b.datum,
        grootboek: b.rekening_code,
        bedrag: b.debet > 0 ? b.debet : -(b.credit || 0),
        omschrijving: b.omschrijving,
      }));

      // Log + generate via backend
      const res = await api.post('/api/v2/export/spreiding', {
        rows: exportRows,
        filename: `spreiding_${kostenRekening}_${startJaar}.xls`,
        metadata: {
          kostenrekening: `${kostenRekening} - ${kostenNaam}`,
          balansrekening: `${balansCode} - ${balansNaam}`,
          bedrag,
          maanden: aantalMaanden,
        },
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `spreiding_${kostenRekening}_${startJaar}.xls`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Export succesvol gedownload en gelogd');
    } catch (err) {
      toast.error('Export mislukt');
    } finally {
      setExporting(false);
    }
  };

  // Preview berekening text
  const previewText = useMemo(() => {
    if (!bedrag || bedrag === 0 || !aantalMaanden) return '';
    const n = aantalMaanden;
    const perMaand = Math.floor((bedrag / n) * 100) / 100;
    const rest = Math.round((bedrag - perMaand * n) * 100) / 100;
    const sm = parseInt(startMaand);
    const sj = parseInt(startJaar);
    let em = sm + n - 1;
    let ej = sj;
    while (em > 12) { em -= 12; ej += 1; }
    const startLabel = MONTHS.find(m => m.value === String(sm).padStart(2, '0'))?.short;
    const endLabel = MONTHS.find(m => m.value === String(em).padStart(2, '0'))?.short;
    return {
      calc: `${fmt(bedrag)} \u00f7 ${n} maanden = ${fmt(perMaand)} per maand${rest !== 0 ? ` (afrondingsverschil ${fmt(Math.abs(rest))} op laatste maand)` : ''}`,
      periode: `Periode: ${startLabel}-${sj} t/m ${endLabel}-${ej}`,
    };
  }, [bedrag, aantalMaanden, startMaand, startJaar]);

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center gap-1 mb-6" data-testid="spreiding-step-indicator">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.num}>
          <button
            onClick={() => { if (s.num < step) setStep(s.num); }}
            disabled={s.num > step}
            data-testid={`step-${s.num}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
              s.num === step
                ? 'bg-[#6366f1] text-white shadow-sm'
                : s.num < step
                  ? 'bg-[#6366f1]/10 text-[#6366f1] cursor-pointer hover:bg-[#6366f1]/20'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              s.num === step ? 'bg-white/20 text-white' : s.num < step ? 'bg-[#6366f1]/20 text-[#6366f1]' : 'bg-gray-200 text-gray-400'
            }`}>{s.num < step ? '✓' : s.num}</span>
            <span className="hidden sm:inline">{s.label}</span>
          </button>
          {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div data-testid="spreidingstool-page">
      <StepIndicator />

      {/* ─── STEP 1: Upload ─── */}
      {step === 1 && (
        <div className="tw1-card p-6" data-testid="spreiding-step1">
          <h2 className="text-lg font-semibold text-[#0b1220] mb-1">Upload Exact Online boekstuk-export</h2>
          <p className="text-sm text-[#526071] mb-4">Sleep een .xlsx of .xls bestand hierheen, of klik om te selecteren.</p>

          <div
            data-testid="spreiding-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-200 ${
              dragging
                ? 'border-[#6366f1] bg-[#6366f1]/5'
                : 'border-gray-300 hover:border-[#6366f1]/50 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              className="hidden"
              data-testid="spreiding-file-input"
            />
            {loading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-3 border-[#6366f1] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-[#526071]">Bestand verwerken...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-[#6366f1]/10 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-[#6366f1]" />
                </div>
                <p className="font-medium text-[#0b1220]">Sleep bestand hierheen</p>
                <p className="text-sm text-[#526071]">of klik om te selecteren (.xlsx / .xls)</p>
              </div>
            )}
          </div>

          {fileName && !loading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-[#526071]">
              <FileSpreadsheet className="w-4 h-4 text-[#6366f1]" />
              <span className="font-mono">{fileName}</span>
            </div>
          )}
        </div>
      )}

      {/* ─── STEP 2: Selecteer kostenregel ─── */}
      {step === 2 && (
        <div data-testid="spreiding-step2">
          {/* Metadata bar */}
          {metadata && (
            <div className="tw1-card p-4 mb-4 flex flex-wrap gap-4 text-sm" data-testid="spreiding-metadata">
              {metadata.administratie && (
                <div><span className="text-[#526071]">Administratie:</span>{' '}<span className="font-medium font-mono">{metadata.administratie}</span></div>
              )}
              {metadata.boekstuknummer && (
                <div><span className="text-[#526071]">Boekstuknummer:</span>{' '}<span className="font-medium font-mono">{metadata.boekstuknummer}</span></div>
              )}
              {metadata.dagboek && (
                <div><span className="text-[#526071]">Dagboek:</span>{' '}<span className="font-medium font-mono">{metadata.dagboek}</span></div>
              )}
              {metadata.periode && (
                <div><span className="text-[#526071]">Periode:</span>{' '}<span className="font-medium font-mono">{metadata.periode}</span></div>
              )}
            </div>
          )}

          <div className="tw1-card overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-[#0b1220]">Selecteer de kostenregel om te spreiden</h2>
              <span className="text-xs text-[#526071]">{rows.length} regels</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full tw1-table" data-testid="spreiding-rows-table">
                <thead>
                  <tr className="bg-[#003366] text-white">
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Nr.</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Grootboekrekening</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Omschrijving</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider">Debet</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider">Credit</th>
                    <th className="px-3 py-2.5 text-center text-xs font-medium uppercase tracking-wider">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const isKosten = isKostenrekening(row.grootboek);
                    const isSelected = idx === selectedIdx;
                    return (
                      <tr
                        key={idx}
                        data-testid={`spreiding-row-${idx}`}
                        onClick={() => isKosten && handleSelectRow(idx)}
                        className={`border-b border-gray-50 transition-colors duration-100 ${
                          isSelected
                            ? 'bg-[#6366f1]/10 ring-1 ring-inset ring-[#6366f1]/30'
                            : isKosten
                              ? 'hover:bg-[#6366f1]/5 cursor-pointer'
                              : 'opacity-60'
                        }`}
                      >
                        <td className="px-3 py-2.5 text-sm font-mono">{row.nr || row.row_num}</td>
                        <td className="px-3 py-2.5 text-sm font-mono">{row.grootboek}</td>
                        <td className="px-3 py-2.5 text-sm">{row.omschrijving}</td>
                        <td className="px-3 py-2.5 text-sm font-mono text-right text-green-700">{row.debet ? fmtNum(row.debet) : ''}</td>
                        <td className="px-3 py-2.5 text-sm font-mono text-right text-red-600">{row.credit ? fmtNum(row.credit) : ''}</td>
                        <td className="px-3 py-2.5 text-center">
                          {isKosten ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#6366f1]/10 text-[#6366f1]">kosten</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => setStep(1)} className="tw1-btn" data-testid="spreiding-back-btn">
              Terug
            </Button>
            <Button
              onClick={() => { if (selectedIdx >= 0) setStep(3); }}
              disabled={selectedIdx < 0}
              className="tw1-btn bg-[#6366f1] hover:bg-[#4f46e5] text-white"
              data-testid="spreiding-next-btn"
            >
              Volgende <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* ─── STEP 3: Instellingen ─── */}
      {step === 3 && (
        <div data-testid="spreiding-step3">
          <div className="tw1-card p-6">
            <h2 className="text-lg font-semibold text-[#0b1220] mb-4">Spreidingsinstellingen</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="space-y-2">
                <Label>Aantal maanden</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={aantalMaanden}
                  onChange={(e) => setAantalMaanden(parseInt(e.target.value) || 1)}
                  className="h-10 font-mono"
                  data-testid="spreiding-aantal-maanden"
                />
              </div>

              <div className="space-y-2">
                <Label>Startmaand</Label>
                <Select value={startMaand} onValueChange={setStartMaand}>
                  <SelectTrigger className="h-10" data-testid="spreiding-start-maand">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Startjaar</Label>
                <Input
                  type="number"
                  min={2020}
                  max={2035}
                  value={startJaar}
                  onChange={(e) => setStartJaar(parseInt(e.target.value) || now.getFullYear())}
                  className="h-10 font-mono"
                  data-testid="spreiding-start-jaar"
                />
              </div>

              <div className="space-y-2">
                <Label>Kostenrekening</Label>
                <Input
                  value={`${kostenRekening}${kostenNaam ? ' - ' + kostenNaam : ''}`}
                  onChange={(e) => {
                    const val = e.target.value;
                    const idx = val.indexOf(' - ');
                    if (idx > 0) {
                      setKostenRekening(val.substring(0, idx).trim());
                      setKostenNaam(val.substring(idx + 3).trim());
                    } else {
                      setKostenRekening(val.trim());
                      setKostenNaam('');
                    }
                  }}
                  className="h-10 font-mono"
                  data-testid="spreiding-kosten-rekening"
                />
              </div>

              <div className="space-y-2">
                <Label>Balansrekening</Label>
                <Select value={balansCode} onValueChange={setBalansCode}>
                  <SelectTrigger className="h-10" data-testid="spreiding-balans-rekening">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BALANS_OPTIES.map(b => (
                      <SelectItem key={b.code} value={b.code}>{b.code} — {b.naam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Te spreiden bedrag</Label>
                <div className="h-10 flex items-center px-3 bg-gray-50 border border-gray-200 rounded-md">
                  <span className="font-mono font-medium text-[#0b1220]" data-testid="spreiding-bedrag">{fmt(bedrag)}</span>
                </div>
              </div>
            </div>

            {/* Preview berekening */}
            {previewText && (
              <div className="bg-[#6366f1]/5 border border-[#6366f1]/20 rounded-xl p-4 mb-6" data-testid="spreiding-preview">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-[#6366f1] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0b1220] font-mono">{previewText.calc}</p>
                    <p className="text-sm text-[#526071] mt-1">{previewText.periode}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="tw1-btn" data-testid="spreiding-back-btn-3">
                Terug
              </Button>
              <Button
                onClick={generateBoekingen}
                className="tw1-btn bg-[#6366f1] hover:bg-[#4f46e5] text-white"
                data-testid="spreiding-genereer-btn"
              >
                Genereer memoriaalboekingen <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── STEP 4: Memoriaalschema ─── */}
      {step === 4 && (
        <div data-testid="spreiding-step4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="tw1-card p-4" data-testid="spreiding-totaal-debet">
              <p className="text-xs text-[#526071] uppercase tracking-wide mb-1">Totaal Debet</p>
              <p className="text-xl font-bold font-mono text-green-700">{fmt(totals.debet)}</p>
            </div>
            <div className="tw1-card p-4" data-testid="spreiding-totaal-credit">
              <p className="text-xs text-[#526071] uppercase tracking-wide mb-1">Totaal Credit</p>
              <p className="text-xl font-bold font-mono text-red-600">{fmt(totals.credit)}</p>
            </div>
            <div className="tw1-card p-4" data-testid="spreiding-balans-check">
              <p className="text-xs text-[#526071] uppercase tracking-wide mb-1">Balans Controle</p>
              {totals.balanced ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-lg font-bold text-green-700">Klopt</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-red-600">Verschil!</span>
                </div>
              )}
            </div>
          </div>

          {/* Boekingen tabel */}
          <div className="tw1-card overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-[#0b1220]">Memoriaalboekingen</h2>
              <span className="text-xs text-[#526071]">{boekingen.length} regels</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full tw1-table" data-testid="spreiding-boekingen-table">
                <thead>
                  <tr className="bg-[#003366] text-white">
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider w-10">#</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Datum</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Type</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Omschrijving</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Rekening</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider">Naam</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider">Debet</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {boekingen.map((b, idx) => {
                    const isOverboeking = b.type === 'Overboeking';
                    return (
                      <tr
                        key={idx}
                        data-testid={`boeking-row-${idx}`}
                        className={`border-b border-gray-50 ${
                          isOverboeking
                            ? 'bg-amber-50/60'
                            : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="px-3 py-2 text-sm font-mono text-[#526071]">{idx + 1}</td>
                        <td className="px-3 py-2 text-sm font-mono">{b.datum}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            isOverboeking
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-[#6366f1]/10 text-[#6366f1]'
                          }`}>
                            {b.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm">{b.omschrijving}</td>
                        <td className="px-3 py-2 text-sm font-mono">{b.rekening_code}</td>
                        <td className="px-3 py-2 text-sm">{b.rekening_naam}</td>
                        <td className="px-3 py-2 text-sm font-mono text-right text-green-700">{b.debet ? fmtNum(b.debet) : ''}</td>
                        <td className="px-3 py-2 text-sm font-mono text-right text-red-600">{b.credit ? fmtNum(b.credit) : ''}</td>
                      </tr>
                    );
                  })}
                  {/* TOTAAL row */}
                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300" data-testid="boeking-totaal-row">
                    <td className="px-3 py-2.5" colSpan={6}>
                      <span className="text-sm uppercase tracking-wide">Totaal</span>
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-green-700">{fmtNum(totals.debet)}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-red-600">{fmtNum(totals.credit)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(3)} className="tw1-btn" data-testid="spreiding-back-btn-4">
              Terug naar instellingen
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="tw1-btn bg-[#6366f1] hover:bg-[#4f46e5] text-white"
              data-testid="spreiding-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporteren...' : 'Exporteer naar Excel'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
